import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { completeSession, recordAttempt, startSession } from "@/api/sessions";
import { getUserSettings } from "@/api/user-settings";
import type { QuestionSetItemRead, SessionCompleteResponse } from "@/api/library-types";
import { grade, gradeParts, hasAnswer, isSelfGraded, type PartGrade } from "./grading";
import { questionRendererFor } from "./renderers/registry";
import { progressStore } from "./progress-store";
import { Button } from "@/components/ui";
import { ContinueChooser } from "@/create/continue-chooser";

/** One playable question with the context the player needs. Both Library
 *  (single-set) and Train (multi-set composed) produce these. */
export interface PlayItem {
  item: QuestionSetItemRead;
  setId: string;
  topicSlug?: string;
  bulkId?: string;
  bulkGradedSequence?: string[];
  /** Originating set's framework slug — gates content features (e.g. word gloss). */
  frameworkSlug?: string | null;
  /** Bulk-level shared passage text (de-trainer reading bulks), when present. */
  sharedPassageText?: string;
}

export function Player({
  items,
  anchorSetId,
  backTo,
  backLabel = "Exit",
  againTo,
  startIndex = 0,
  onCompleted,
}: {
  items: PlayItem[];
  anchorSetId: string;
  backTo: string;
  backLabel?: string;
  againTo?: string;
  /** Where to start in `items` — used to resume a set from its saved cursor. */
  startIndex?: number;
  /** Fired after the session is completed server-side, with the session id —
   *  used by the placement flow to score the diagnostic. Additive; normal
   *  callers omit it. */
  onCompleted?: (sessionId: string, summary: SessionCompleteResponse) => void;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [sessionId, setSessionId] = useState<string | null>(null);
  const startedRef = useRef(false);
  const startMut = useMutation({
    mutationFn: () => startSession(anchorSetId),
    onSuccess: (r) => setSessionId(r.session_id),
  });
  useEffect(() => {
    if (!startedRef.current && items.length > 0 && !sessionId) {
      startedRef.current = true;
      startMut.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  // Persist the progress tail when the player unmounts — leaving a session
  // mid-way via the "✕ Exit" link is an in-app navigation (no pagehide), so
  // without this the answers since the last write wait on the 5s debounce and
  // can be lost if the user logs out / closes the tab first.
  useEffect(() => {
    return () => {
      void progressStore.flush();
    };
  }, []);

  // Running sum of fractional minutes accrued this session (shown on Results;
  // the integer tank only moves when the server carry crosses a whole minute).
  const [sessionAccrued, setSessionAccrued] = useState(0);
  // Last server-confirmed reward, tagged with the question index it belongs to so
  // the per-question chip never shows a stale value after navigating.
  const [lastReward, setLastReward] = useState<
    { index: number; minutes: number; multiplier: number } | null
  >(null);
  const attemptMut = useMutation({
    mutationFn: (vars: {
      sessionId: string;
      qIndex: number;
      input: Parameters<typeof recordAttempt>[1];
    }) => recordAttempt(vars.sessionId, vars.input),
    onSuccess: (data, vars) => {
      setSessionAccrued((s) => s + (data.minutes_accrued ?? 0));
      setLastReward({
        index: vars.qIndex,
        minutes: data.minutes_accrued ?? 0,
        multiplier: data.multiplier_used ?? 1,
      });
    },
  });
  const completeMut = useMutation({
    mutationFn: () => completeSession(sessionId as string),
    onSuccess: (r) => {
      setFinished(r);
      if (sessionId) onCompleted?.(sessionId, r);
    },
  });

  // Word-gloss "translate answers" setting (shared with iOS via /user-settings).
  const settingsQ = useQuery({ queryKey: ["user-settings"], queryFn: getUserSettings });
  const translateAnswers = settingsQ.data?.translate_answers_enabled ?? false;

  const [index, setIndex] = useState(startIndex);
  const [answer, setAnswer] = useState<unknown>(undefined);
  const [revealed, setRevealed] = useState(false);
  const [correct, setCorrect] = useState<boolean | null>(null);
  const [results, setResults] = useState<boolean[]>([]);
  const [finished, setFinished] = useState<SessionCompleteResponse | null>(null);
  // Snapshot of each visited question's answer/reveal so Back can review it.
  const [byIndex, setByIndex] = useState<
    Record<number, { answer: unknown; revealed: boolean; correct: boolean | null }>
  >({});
  const questionStart = useRef<number>(0);
  const attempted = useRef<Set<number>>(new Set());

  const entry = items[index];
  const type = entry?.item.question_type ?? "";
  const content = entry?.item.content ?? {};

  useEffect(() => {
    questionStart.current = Date.now();
  }, [index]);

  const postAttempt = (correctVal: boolean, pg?: PartGrade | null) => {
    if (!entry || attempted.current.has(index)) return;
    attempted.current.add(index);
    setResults((r) => [...r, correctVal]);
    if (sessionId) {
      // Partial grading: multi-part items report the per-element split so the
      // server scores on the fraction correct. Single-response items omit these
      // (pg == null) and post the legacy binary shape.
      const total = pg ? pg.correctIds.length + pg.wrongIds.length : 0;
      attemptMut.mutate({
        sessionId,
        qIndex: index,
        input: {
          questionId: entry.item.id,
          correct: correctVal,
          responseTimeMs: Math.max(0, Date.now() - questionStart.current),
          topicSlug: entry.topicSlug,
          ...(pg && total > 0
            ? {
                score: pg.correctIds.length / total,
                correctParts: pg.correctIds.length,
                totalParts: total,
              }
            : {}),
        },
      });
    }
    // Two-way progress write (no-op until WRITE_ENABLED).
    if (entry.bulkId) {
      progressStore.recordOutcome(entry.setId, entry.item.id, correctVal, {
        bulkId: entry.bulkId,
        bulkGradedSequence: entry.bulkGradedSequence ?? [],
      });
    }
  };

  const onCheck = () => {
    setRevealed(true);
    if (!isSelfGraded(type)) {
      // Per-element split for multi-part types (null for single-response).
      const pg = gradeParts(type, content, answer);
      const c = pg ? pg.wrongIds.length === 0 : grade(type, content, answer);
      setCorrect(c);
      postAttempt(c, pg);
    }
  };
  const onSelfMark = (val: boolean) => {
    setCorrect(val);
    postAttempt(val);
  };

  const isLast = index >= items.length - 1;
  const goTo = (target: number) => {
    setByIndex((m) => ({ ...m, [index]: { answer, revealed, correct } }));
    const saved = byIndex[target];
    setIndex(target);
    setAnswer(saved ? saved.answer : undefined);
    setRevealed(saved ? saved.revealed : false);
    setCorrect(saved ? saved.correct : null);
  };
  const onNext = () => {
    if (isLast) {
      void progressStore.flush();
      if (sessionId) completeMut.mutate();
      else setFinished(localSummary(results));
      return;
    }
    goTo(index + 1);
  };
  const onBack = () => {
    if (index > 0) goTo(index - 1);
  };

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center">
        <p className="text-ink-faint">{t("Nothing to practise here.")}</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate(backTo)}>
          {t("Back")}
        </Button>
      </div>
    );
  }

  if (finished) {
    return (
      <Results
        result={finished}
        total={items.length}
        accrued={sessionAccrued}
        backTo={backTo}
        againTo={againTo}
        frameworkSlug={items[0]?.frameworkSlug}
        continueFromSetId={anchorSetId}
      />
    );
  }

  const Renderer = questionRendererFor(type);
  const correctCount = results.filter(Boolean).length;

  return (
    <div className="mx-auto max-w-2xl lg:max-w-4xl xl:max-w-5xl">
      <div className="mb-6">
        <div className="flex items-center justify-between text-sm text-ink-faint">
          <Link to={backTo} className="hover:text-ink">
            ✕ {t(backLabel)}
          </Link>
          <span>
            {t("{{index}} / {{total}} · {{correct}} correct", {
              index: index + 1,
              total: items.length,
              correct: correctCount,
            })}
          </span>
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-brand-100">
          <div
            className="h-full rounded-full bg-brand-500 transition-all"
            style={{ width: `${((index + (revealed ? 1 : 0)) / items.length) * 100}%` }}
          />
        </div>
      </div>

      <div className="card p-5 sm:p-6">
        <Renderer
          key={entry.item.id}
          type={type}
          itemId={entry.item.id}
          content={content}
          answer={answer}
          setAnswer={setAnswer}
          revealed={revealed}
          correct={correct}
          frameworkSlug={entry.frameworkSlug}
          translateAnswers={translateAnswers}
          sharedPassageText={entry.sharedPassageText}
        />
      </div>

      {revealed && correct !== null && (
        <div
          className={`mt-4 flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm font-semibold ${
            correct ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
          }`}
        >
          <span>{correct ? t("Correct! 🎉") : t("Not quite — review and keep going.")}</span>
          {correct &&
            lastReward?.index === index &&
            lastReward.minutes > 0 && (
              <span className="shrink-0 rounded-full bg-emerald-100 px-2.5 py-1 text-xs text-emerald-800">
                {t("+{{minutes}} min", { minutes: lastReward.minutes.toFixed(1) })}
                {lastReward.multiplier !== 1 && ` · ${lastReward.multiplier}×`}
              </span>
            )}
        </div>
      )}

      <div className="mt-5 flex items-center justify-end gap-3">
        {index > 0 && (
          <Button variant="ghost" size="lg" className="mr-auto" onClick={onBack}>
            ← {t("Back")}
          </Button>
        )}
        {!revealed && (
          <Button
            size="lg"
            disabled={!hasAnswer(type, content, answer) || startMut.isPending}
            onClick={onCheck}
          >
            {t("Check")}
          </Button>
        )}
        {revealed && correct === null && (
          <>
            <span className="mr-auto text-sm text-ink-faint">{t("How did you do?")}</span>
            <Button variant="secondary" size="lg" onClick={() => onSelfMark(false)}>
              {t("Missed it")}
            </Button>
            <Button size="lg" onClick={() => onSelfMark(true)}>
              {t("Got it")}
            </Button>
          </>
        )}
        {revealed && correct !== null && (
          <Button size="lg" loading={completeMut.isPending} onClick={onNext}>
            {isLast ? t("Finish") : t("Next")}
          </Button>
        )}
      </div>
    </div>
  );
}

function localSummary(results: boolean[]): SessionCompleteResponse {
  return {
    session_id: "",
    xp_awarded: 0,
    tier_before: "",
    tier_after: "",
    streak_after: 0,
    streak_delta: 0,
    total_xp: 0,
    session_score: 0,
    attempt_count: results.length,
    correct_count: results.filter(Boolean).length,
    total_minutes_earned: 0,
  };
}

function Results({
  result,
  total,
  accrued,
  backTo,
  againTo,
  frameworkSlug,
  continueFromSetId,
}: {
  result: SessionCompleteResponse;
  total: number;
  /** Fractional minutes accrued this session (sum of per-attempt minutes_accrued). */
  accrued?: number;
  backTo: string;
  againTo?: string;
  frameworkSlug?: string | null;
  continueFromSetId?: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canContinue = frameworkSlug === "language-trainer" && !!continueFromSetId;
  const pct = total > 0 ? Math.round((result.correct_count / total) * 100) : 0;
  // The tank only takes whole minutes; surface the fractional effort credited so a
  // session of quick drills that banked sub-minute remainders doesn't read as "+0".
  const showAccrued =
    typeof accrued === "number" && accrued > 0 && accrued > result.total_minutes_earned;
  return (
    <div className="mx-auto max-w-md py-10 text-center animate-fade-up">
      <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100 text-3xl">
        🏁
      </div>
      <h1 className="text-2xl font-bold tracking-tight">{t("Session complete")}</h1>
      <p className="mt-1 text-ink-faint">
        {t("{{correct}} of {{total}} correct ({{pct}}%)", {
          correct: result.correct_count,
          total: result.attempt_count,
          pct,
        })}
      </p>
      <div className="mt-6 grid grid-cols-3 gap-3">
        <Stat label={t("XP")} value={`+${result.xp_awarded}`} />
        <Stat label={t("Streak")} value={`${result.streak_after}🔥`} />
        <Stat label={t("Minutes")} value={`+${result.total_minutes_earned}`} />
      </div>
      {showAccrued && (
        <p className="mt-3 text-xs text-ink-faint">
          {t(
            "+{{minutes}} min of effort credited — the remainder banks toward your next unlocked minute.",
            { minutes: accrued!.toFixed(1) },
          )}
        </p>
      )}
      <div className="mt-8 flex justify-center gap-3">
        <Link to={backTo}>
          <Button variant="secondary" size="lg">
            {t("Done")}
          </Button>
        </Link>
        {againTo && (
          <Link to={againTo}>
            <Button size="lg">{t("Again")}</Button>
          </Link>
        )}
      </div>
      {canContinue && (
        <div className="card mt-6 p-4 text-left">
          <h2 className="text-sm font-bold tracking-tight">{t("Continue your course")}</h2>
          <p className="mt-0.5 text-xs text-ink-faint">
            {t("Build the next set from where you are — pick a direction.")}
          </p>
          <div className="mt-3">
            <ContinueChooser
              baseInputs={{ continue_from_set_id: continueFromSetId! }}
              onStarted={() => navigate("/app/create")}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card p-4">
      <p className="text-lg font-bold text-ink">{value}</p>
      <p className="text-xs text-ink-faint">{label}</p>
    </div>
  );
}
